import React from "react";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import goldenStarFilled from "../../../assets/timetrails/golden_star_filled.svg";
import timerIcon from "../../../assets/timetrails/timerIcon.svg";
import { TimeTrialModal } from "./CreateTimeTrial";
import { formatDateForCard, formatStatus } from "../../../utils";
import WorkspaceDrawer from "./WorkspaceDrawer";
import { DeleteModal } from "../../UI/Delete Modal/DeleteModal";
import { useAlert } from "../../../hooks/useAlert";
import { useDeleteTimeTrailMutation } from "../../../redux-toolkit/features/time-trails/timeTrailsApiSlice";
import "./TimeTrailCard.css";
import { useCloseOnCompanySwitch } from "../../../utils/companyContextNavigation";

// Utility function to determine status based on startDate and endDate
const getStatusFromEndDate = (startDate, endDate) => {
  if (!startDate || !endDate) return "Inactive";

  const now = new Date();
  const start = new Date(startDate);
  const end = new Date(endDate);

  if (start > now) {
    return "Scheduled";
  } else if (end > now) {
    return "Active";
  } else {
    return "Inactive";
  }
};

// Utility function to determine status type for CSS classes
const getStatusType = (status) => {
  switch (status?.toLowerCase()) {
    case "active":
      return "active";
    case "scheduled":
      return "scheduled";
    case "expired":
    case "inactive":
      return "inactive";
    default:
      return "inactive";
  }
};

// Utility function to format time difference for admin display (removes unnecessary zeros)
const formatTimeForAdmin = (startDate, endDate) => {
  if (!startDate || !endDate) {
    return "Invalid dates";
  }

  try {
    const start = new Date(startDate);
    const end = new Date(endDate);

    // Check if dates are valid
    if (isNaN(start.getTime()) || isNaN(end.getTime())) {
      return "Invalid dates";
    }

    const diffInMs = end.getTime() - start.getTime();
    const diffInMinutes = Math.floor(diffInMs / (1000 * 60));

    if (diffInMinutes < 0) {
      return "End date is before start date";
    }

    const totalHours = Math.floor(diffInMinutes / 60);
    const minutes = diffInMinutes % 60;

    if (totalHours > 0) {
      if (minutes === 0) {
        return `${totalHours}h`;
      }
      return `${totalHours}h:${minutes.toString().padStart(2, "0")}m`;
    }

    if (minutes > 0) {
      return `${minutes}m`;
    }

    return "0m";
  } catch (error) {
    console.error("Error calculating time difference:", error);
    return "Error calculating time";
  }
};

// Utility function to calculate remaining time for active time trials
const calculateRemainingTime = (endDate, currentTime = new Date()) => {
  if (!endDate) return null;

  const now = currentTime;
  const end = new Date(endDate);
  const remainingTime = end.getTime() - now.getTime();

  if (remainingTime <= 0) return null;

  const hours = Math.floor(remainingTime / (1000 * 60 * 60));
  const minutes = Math.floor((remainingTime % (1000 * 60 * 60)) / (1000 * 60));
  const seconds = Math.floor((remainingTime % (1000 * 60)) / 1000);

  if (hours > 0) {
    return `${hours.toString().padStart(2, "0")}h:${minutes
      .toString()
      .padStart(2, "0")}m:${seconds.toString().padStart(2, "0")}s`;
  } else if (minutes > 0) {
    return `${minutes.toString().padStart(2, "0")}m:${seconds
      .toString()
      .padStart(2, "0")}s`;
  } else {
    return `00:${seconds.toString().padStart(2, "0")}s`;
  }
};

export const TimeTrailCard = ({
  data,
  selectedCompany,
  setDrawer,
  drawer,
  handleTimeTrialOpen,
  refetch,
}) => {
  const [deleteOpen, setDeleteOpen] = React.useState(false);
  const [editOpen, setEditOpen] = React.useState(false);
  const [previewOpen, setPreviewOpen] = React.useState(false);
  const [currentTime, setCurrentTime] = React.useState(new Date());

  const [deleteTimeTrail] = useDeleteTimeTrailMutation();
  const { displayAlert } = useAlert();

  useCloseOnCompanySwitch(() => {
    setEditOpen(false);
    setPreviewOpen(false);
  });

  // Real-time countdown timer for active time trials
  React.useEffect(() => {
    const status = getStatusFromEndDate(data?.startDate, data?.endDate);
    const isActive = status === "Active";

    if (isActive && data?.endDate) {
      const interval = setInterval(() => {
        setCurrentTime(new Date());
      }, 1000);

      return () => clearInterval(interval);
    }
  }, [data?.startDate, data?.endDate]);

  const editTimeTrialHandler = () => {
    setEditOpen(true);
    setDrawer(false);
  };

  const previewTimeTrialHandler = () => {
    setPreviewOpen(true);
    setDrawer(false);
  };

  const deleteTimeTrialHandler = async () => {
    try {
      await deleteTimeTrail({
        trailId: data._id,
      }).unwrap();

      displayAlert({
        open: true,
        message: "Time trial deleted successfully!",
        type: "success",
        timeout: "3000",
      });

      setDeleteOpen(false);
      refetch();
    } catch (error) {
      console.error("Error deleting time trial:", error);
      displayAlert({
        open: true,
        message: error?.data?.message || "Failed to delete time trial",
        type: "error",
        timeout: "3000",
      });
    }
  };

  const status = getStatusFromEndDate(data?.startDate, data?.endDate);
  const isActive = status === "Active";
  const remainingTime = calculateRemainingTime(data?.endDate, currentTime);

  return (
    <>
      <div className="timetrial-card">
        {/* Purple header overlay for active time trials */}
        {isActive && remainingTime && (
          <div className="timetrial-card__purple-header">
            <div className="timetrial-card__time-display">
              <img
                src={timerIcon}
                alt="Timer"
                style={{
                  width: "16px",
                  height: "16px",
                  filter: "brightness(0) invert(1)",
                }}
              />
              <span className="timetrial-card__time-text">{remainingTime}</span>
            </div>
          </div>
        )}

        <div className="timetrial-card__header">
          <div className="timetrial-card__date">
            <CalendarTodayIcon
              sx={{ fontSize: "16px", color: "var(--grey600)" }}
            />
            <span className="timetrial-card__date-text">
              {formatDateForCard(data?.startDate)}
            </span>
          </div>
          <div className="timetrial-card__more">
            {drawer !== data._id && (
              <MoreVertIcon
                onClick={() => setDrawer(data._id)}
                sx={{
                  fontSize: "20px",
                  color: "var(--grey600)",
                  cursor: "pointer",
                }}
              />
            )}
            {drawer === data._id && (
              <WorkspaceDrawer
                editbtnHandler={
                  status === "Scheduled" ? editTimeTrialHandler : undefined
                }
                previewbtnHandler={previewTimeTrialHandler}
                viewbtnHandler={() => {}} // Not needed for time trials
                deletebtnHandler={
                  status === "Scheduled" ? () => setDeleteOpen(true) : undefined
                }
                setDrawer={setDrawer}
                isView={false}
                editLabel="Edit Task"
                previewLabel="Preview"
                deleteLabel="Delete Task"
                showEdit={status === "Scheduled"}
                showDelete={status === "Scheduled"}
              />
            )}
            <DeleteModal
              label={data?.task}
              deleteHandler={deleteTimeTrialHandler}
              setDeleteOpen={setDeleteOpen}
              deleteOpen={deleteOpen}
              deleteType="time trial"
            />
          </div>
        </div>

        <div className="timetrial-card__content">
          <p className="timetrial-card__description">
            {data?.task || "No description available"}
          </p>
        </div>

        <div className="timetrial-card__footer">
          {/* <div className="timetrial-card__footer-left"> */}
          <div className="timetrial-card__points">
            <img
              src={goldenStarFilled}
              alt="Golden Star"
              style={{ width: "15px", height: "15px" }}
            />
            <span className="timetrial-card__points-text">
              {data?.points} Points
            </span>
          </div>
          <div className="timetrial-card__separator">•</div>
          <div className="timetrial-card__time">
            <span className="timetrial-card__points-text">
              Time: {formatTimeForAdmin(data?.startDate, data?.endDate)}
            </span>
          </div>
          {/* </div> */}

          <div className="timetrial-card__separator">•</div>

          <div className="timetrial-card__status">
            <span
              className={`timetrial-card__status-text ${getStatusType(status)}`}
            >
              {formatStatus(status)}
            </span>
          </div>
        </div>
      </div>

      {/* Unified TimeTrialModal for Edit and Preview */}
      <TimeTrialModal
        timeTrialOpen={editOpen}
        setTimeTrialOpen={setEditOpen}
        editData={data}
        isEditMode={true}
        selectedCompany={selectedCompany}
        refetch={refetch}
      />

      <TimeTrialModal
        timeTrialOpen={previewOpen}
        setTimeTrialOpen={setPreviewOpen}
        editData={data}
        isEditMode={true}
        isPreviewMode={true}
      />
    </>
  );
};
